import { CreditRuleCoin } from '@aibees/elephant/components';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { LoadingButton } from '@mui/lab';
import { memo, useEffect, useMemo, useRef } from 'react';
import { useHandleUnlockEmails } from '../../hooks/use-handle-unlock-emails';
import * as Styled from './styles';
import * as SX from './sx';
import type { UnlockEmailsProps as Props } from './types';

export const UnlockEmails = memo(
  ({ className, compact, item, setLoading }: Props) => {
    const hover = useBoolean(false);

    const {
      handleReverify,
      handleUpdate,
      hasUpdate,
      needsEnrichEmail,
      notFoundRetryable,
      showReverify,
      unlocked,
      loading,
      handleUnlock,
      handleEnrichEmail,
      creditType,
    } = useHandleUnlockEmails(item);

    useEffect(() => {
      setLoading?.(loading);
    }, [loading]);

    const action = useMemo(() => {
      // Above the Unlock arm on purpose: a company change outranks the email's
      // own state, so a never-unlocked contact whose company changed gets the
      // combined action rather than a plain Unlock.
      if (needsEnrichEmail)
        return {
          label: 'Update Profile & Find Email',
          handler: handleEnrichEmail,
        };
      if (!unlocked) return { label: 'Unlock', handler: handleUnlock };
      if (hasUpdate) return { label: 'Update', handler: handleUpdate };
      // Below the company-change arm on purpose: a record with both a job change
      // and a 21-day-old unfound email gets the combined action, not Retry. The
      // handler is the ordinary unlock — the only thing the cooldown changes is
      // whether the button is offered at all.
      if (notFoundRetryable) return { label: 'Retry', handler: handleUnlock };
      if (showReverify) return { label: 'Re-Verify', handler: handleReverify };
      return null;
    }, [
      unlocked,
      hasUpdate,
      needsEnrichEmail,
      notFoundRetryable,
      showReverify,
      handleUnlock,
      handleUpdate,
      handleEnrichEmail,
      handleReverify,
    ]);

    const handleClick = (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      action?.handler(e);
      hover.onFalse();
    };

    // Preserve last action's label during loading so the button keeps its
    // natural width (MUI hides the text via `color: transparent` but the width
    // is still occupied) and we avoid a layout jump on the way out.
    const lastActionRef = useRef(action);
    if (action) lastActionRef.current = action;
    const displayLabel = action?.label ?? lastActionRef.current?.label;

    return (
      <Styled.Wrapper
        className={classnames('UnlockEmails', className)}
        sx={SX.root(!!compact)}
        direction="row"
        justifyContent={compact ? 'unset' : 'space-between'}
        alignItems="center"
        gap={0.5}
      >
        {hover.value && !loading && (
          <CreditRuleCoin type={creditType} sx={SX.creditTag} />
        )}

        {(action || loading) && (
          <LoadingButton
            variant="contained"
            color="primary"
            size="xsmall"
            loading={loading}
            onClick={handleClick}
            onMouseEnter={hover.onTrue}
            onMouseLeave={hover.onFalse}
          >
            {displayLabel}
          </LoadingButton>
        )}
      </Styled.Wrapper>
    );
  }
);

UnlockEmails.displayName = 'UnlockEmails';
